import { MarkOptionsArg, PerformanceLogPayload, Timing } from 'types/instrumentation';

// handle different levels of performance API support
export const isMarkable =
  typeof performance.mark === 'function' &&
  typeof performance.measure === 'function' &&
  typeof performance.getEntries === 'function' &&
  typeof performance.getEntriesByName === 'function' &&
  typeof performance.getEntriesByType === 'function' &&
  typeof performance.clearMeasures === 'function';

const generatePageLoadProfile = () => {
  const page = {
    TTFB: 0,
    TTFI: 0
  };

  /* START GENAI@GHCOPILOT */
  const navigationEntry = performance.getEntriesByType(
    'navigation'
  )[0] as PerformanceNavigationTiming;

  page.TTFB = navigationEntry.responseStart - navigationEntry.startTime;
  page.TTFI = navigationEntry.domInteractive - navigationEntry.startTime;
  /* END GENAI@GHCOPILOT */
  return page;
};

const handleBrowserAPIs = (api: { [key: string]: string }): string | { [key: string]: string } => {
  // eslint-disable-next-line
  if (api) {
    const ret: { [key: string]: string } = {};
    for (const k in api) {
      if (['string', 'number'].indexOf(typeof api[k]) !== -1) {
        ret[k] = api[k];
      }
    }

    return ret;
  }

  return 'not supported';
};

// called from merchant/PSP domain (visa-sdk) as well as Visa domain (communicator)
export const buildClientProfile = (correlationId: string | undefined) => {
  if (isMarkable) {
    const CR = correlationId || window.VISA_SDK.correlationId;
    const nav = navigator as Navigator;
    const payload = {
      correlationId: CR,
      device: {
        // @ts-ignore - Navigation type is outdated
        conn: handleBrowserAPIs(nav.connection), // blink-only
        cpuCores: nav.hardwareConcurrency,
        locale: nav.language,
        // @ts-ignore - Navigation type is outdated
        mem: nav.deviceMemory ? `${nav.deviceMemory}GB` : 'not supported',
        ua: nav.userAgent
      },
      domains: {},
      page: generatePageLoadProfile()
    };

    /* START GENAI@GHCOPILOT */
    const latencyByDomain: {
      [domain: string]: {
        latency: number;
        totalRequests: number;
        averageLatency: number;
        assets: {
          url: string;
          latency: number;
        }[];
      };
    } = {};

    const resources = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
    resources.forEach((resource: PerformanceResourceTiming) => {
      const latency: number = resource.responseEnd - resource.fetchStart;
      const domain = new URL(resource.name).hostname as string;

      // eslint-disable-next-line
      if (!latencyByDomain[domain]) {
        latencyByDomain[domain] = {
          assets: [],
          averageLatency: 0,
          latency: 0,
          totalRequests: 0
        };
      }

      latencyByDomain[domain].latency += latency;
      latencyByDomain[domain].totalRequests++;
      latencyByDomain[domain].assets.push({ url: resource.name, latency });
    });

    for (const domain in latencyByDomain) {
      latencyByDomain[domain].averageLatency = Math.round(
        latencyByDomain[domain].latency / latencyByDomain[domain].totalRequests
      );

      // @ts-ignore - no need for aggregate latency
      delete latencyByDomain[domain].latency;
    }

    /* END GENAI@GHCOPILOT */
    payload.domains = latencyByDomain;
    return payload;
  }

  return null;
};

export const buildEventPayload = (args: {
  key: string;
  options: MarkOptionsArg;
  timings: Timing;
}): PerformanceLogPayload | null => {
  let measure;
  const { key, options, timings } = args;
  const { failure, reason } = options;
  const endKey = `${key}_end`;
  const startKey = `${key}_start`;
  timings[endKey] = timings[endKey] || Date.now();
  isMarkable && performance.mark(endKey);
  const etime = timings[endKey] || NaN;
  const stime = timings[startKey] || NaN;
  const ttaken = etime - stime;

  if (!options.manualTiming) {
    // undef or `bool:true`
    try {
      measure = isMarkable && performance.measure(key, startKey, endKey);
    } catch (err) {
      /* istanbul ignore next */
      // eslint-disable-next-line no-console
      console.warn(err);
    }
  }

  if (isNaN(ttaken)) {
    // eslint-disable-next-line no-console
    console.warn(`refusing to send bad performance data for ${key}`); // eslint-disable no-console
    return null;
  }

  /* eslint-disable sort-keys */
  return {
    E: `${key}_${failure ? 'FAILURE' : 'SUCCESS'}`,
    ERR: reason,
    ETIME: etime,
    STIME: stime,
    TTAKEN: ttaken,
    TY: 'PERFORMANCE',
    U: window.location.origin,
    mt: measure ? measure.toJSON() : undefined,
    options
  };
};
